import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useDragScroll } from './useDragScroll';
import { TABLE, TABLE_CELL, TABLE_HEAD, TableCard } from './TableCard';
import { CallBadge } from './CallBadge';
import { reasoningTooltip } from './ReasoningBadge';
import { HolesGrid, holeRows, twiceIdsOf } from './HolesGrid';
import { useAssemblySteps } from './categoryBands';
import { useCategories } from './catalogCategories';
import { buildInteractionsPrompt, isHardwarePart } from './hardwareInteractionsPrompt';
import {
  findHardwareInteractions, interactionsContextFor, touchRows, useHardwareInteractions, type EntrySide, type HardwareAlone, type TableHole,
} from './hardwareInteractionsRun';
import { STEPS_MODEL } from './models';
import { namedIn, shownId } from './partsCatalogue';
import { usePartsRun } from './partsRun';
import { STEPS_PHASE, useWorkingElsewhere } from './phaseWorking';
import { useStepSections } from './stepSections';
import { useStepsRun, subStepLabel } from './stepsRun';
import { useSince } from './waitClock';
import { useLanguage } from '../../contexts/LanguageContext';

// A ROW'S SUB-STEPS, TWO TO A LINE — the last alone and centred when there is an odd one out.
// A set repeated across the manual lists a dozen, and on one line that column was wider than
// the page; wrapped to fit, the lines held two, then one, then two, by the width of the labels.
function Substeps({ labels }: { labels: string[] }) {
  if (!labels.length) return <>—</>;
  return (
    <div className="mx-auto grid w-max grid-cols-2 place-items-center gap-x-2 gap-y-0.5 [&>*:last-child:nth-child(odd)]:col-span-2">
      {labels.map((s) => <span key={s}>{s}</span>)}
    </div>
  );
}

type SingleRow = { id: string; substeps: string[]; entry: HardwareAlone | null };

// THE HARDWARE TABLE'S CALL, as a badge: its Run, its question and its answer. Over the
// phase's subphase selector with the phase's other badges, not over the table — every call a
// phase can make is on show whichever subphase is open (see StepsSubphases).
export function HardwareTableBadge({ docId, file, locked }: { docId: string; file: Blob; locked?: boolean }) {
  const { t } = useLanguage();
  const { steps } = useAssemblySteps(docId);
  const st = useStepsRun(docId);
  const { items } = usePartsRun(docId);
  const found = useHardwareInteractions(docId);
  const sections = useStepSections(file, steps);
  // Subscribed so the prompt rebuilds once the ontology lands: until then every category reads
  // as a component and the hardware list is empty.
  const cats = useCategories();

  const doneSteps = steps.some((s) => (st.subSteps[s.category] ?? []).length > 0);
  const elsewhere = useWorkingElsewhere(docId);
  const canRun = doneSteps && !st.busy && !found.busy && !locked && elsewhere?.phase !== STEPS_PHASE;
  const run = () => {
    if (canRun) void findHardwareInteractions(docId, file, steps, st.subSteps, items, STEPS_MODEL, t);
  };
  const running = useSince(found.startedAt);
  const took = found.startedAt != null ? running : found.elapsed;

  const prompt = useMemo(
    () => (doneSteps ? buildInteractionsPrompt(interactionsContextFor(steps, st.subSteps, items)) : ''),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [doneSteps, steps, st.subSteps, items, cats],
  );

  return (
    <CallBadge
      title={t('notice.hardwareTable.badge')}
      named
      run={{ label: t('notice.hardwareTable.run'), onRun: run, busy: found.busy, disabled: !canRun, elapsed: took }}
      promptLabel={t('notice.hardwareTable.promptLabel')}
      // Before the steps are analysed there is no question to show: says what it waits on.
      load={() => Promise.resolve({
        prompt: prompt || t('notice.hardwareTable.needSteps'),
        urls: steps.flatMap((s) => ((st.subSteps[s.category] ?? []).length
          ? sections[s.category] ?? [] : [])),
        model: STEPS_MODEL,
      })}
      answer={found.raw ? {
        label: t('notice.cat.reasoningTitle'),
        content: reasoningTooltip(found.reasoning, found.raw, t, null),
      } : undefined}
    />
  );
}

// PHASE 5'S HARDWARE TABLE: one row per set of hardware parts that work together, the sub-steps
// where the second, third... of them is added, and how they engage — see
// hardwareInteractionsPrompt. Its own run (HardwareTableBadge), and "Analyze every step" runs it
// after the analysis.
export function HardwareTable({ docId }: { docId: string }) {
  const { t, language } = useLanguage();
  const { steps } = useAssemblySteps(docId);
  const st = useStepsRun(docId);
  const { items } = usePartsRun(docId);
  const found = useHardwareInteractions(docId);
  // AN ID TWO HOLES CLAIM, over the whole table — said in the warning colour where it is shown.
  const twiceIds = useMemo(() => twiceIdsOf(touchRows(found).flatMap((r) => r.holes)), [found]);
  // Subscribed so the rows rebuild once the ontology lands.
  const cats = useCategories();

  // THE HARDWARE IN NO INTERACTION, one row each, so every piece of hardware is on the table.
  // Not asked of the model: a part missing from every row it answered is the answer. Its
  // sub-steps are the ones the analysis puts it in — or, for one side of a mirror pair (see
  // EntrySide), the ones the call gave that side: one row per "alone" entry, so a screw into a
  // left and a right rail is two rows.
  const singles = useMemo(() => {
    if (!found.asked) return [];
    const inOne = new Set(found.interactions.flatMap((one) => one.hardware));
    return items.flatMap((p, i): SingleRow[] => {
      if (!isHardwarePart(p) || inOne.has(String(i))) return [];
      const substeps = steps.flatMap((s) => (st.subSteps[s.category] ?? [])
        .filter((sub) => (sub.parts ?? []).some((u) => u.id === String(i)))
        .map((sub) => subStepLabel(s.num, sub.letter)));
      const entries = found.alone.filter((a) => a.hardware === String(i));
      if (!entries.length) return [{ id: String(i), substeps, entry: null }];
      return entries.map((entry) => ({
        id: String(i), substeps: entry.side ? entry.substeps : substeps, entry,
      }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [found.asked, found.interactions, found.alone, items, steps, st.subSteps, cats]);

  // IN THE MANUAL'S ORDER: every row, sets and lone hardware alike, by the first sub-step it
  // happens in. A row naming no sub-step the analysis knows goes last.
  const rows = useMemo(() => {
    const rank = new Map(steps.flatMap((s) => (st.subSteps[s.category] ?? [])
      .map((sub) => subStepLabel(s.num, sub.letter))).map((label, i) => [label, i]));
    const first = (subs: string[]) => Math.min(Infinity, ...subs.map((l) => rank.get(l) ?? Infinity));
    // EACH ENTRY ITS ROW, a mirror pair's two sides included: the call answers one entry per
    // side, each with its own sub-steps and its own holes (see EntrySide) — a split drawn here
    // could only copy one answer twice, and the seat both rails screw into came out with every
    // rail's holes in each copy.
    return [
      ...found.interactions.map((one) => ({ set: one, single: null, side: one.side, substeps: one.substeps })),
      ...singles.map((one) => ({ set: null, single: one, side: one.entry?.side ?? null, substeps: one.substeps })),
    ].map((r) => ({ ...r, at: first(r.substeps) })).sort((a, b) => a.at - b.at);
  }, [found.interactions, singles, steps, st.subSteps]);

  // ENTRIES THAT ARE ONE GESTURE, drawn as one row: the same hardware added in the same
  // sub-steps — a felt pad on the front frame's feet and one on the rear frame's, when the
  // sub-steps never said which foot. The hardware and the sub-steps are one cell spanning them,
  // the count in it their sum; what each does and the holes it makes stay its own. Grouped
  // wherever they sit: equal sub-steps sort to the same place, but not always side by side.
  const groups = useMemo(() => {
    const byKey = new Map<string, (typeof rows)[number][]>();
    rows.forEach((r, i) => {
      const hardware = r.set ? r.set.hardware : [r.single!.id];
      const key = r.substeps.length
        ? JSON.stringify([!!r.set, [...hardware].sort(), [...(r.set?.covers ?? [])].sort(), [...r.substeps].sort()])
        : `row-${i}`;
      byKey.set(key, [...(byKey.get(key) ?? []), r]);
    });
    return [...byKey.values()];
  }, [rows]);
  // A group's count of one piece: the sum over its entries, null where none of them said.
  const sumOf = (counts: (number | null | undefined)[]) =>
    (counts.some((n) => n) ? counts.reduce<number>((a, n) => a + (n ?? 0), 0) : null);

  // A part as the table names it: its shown id and its name in the reader's language.
  // `count`: how many the row uses, in the bracket with the letter — "JCBC screw (E × 2)".
  const nameOf = (id: string, count?: number | null) => {
    const p = items[Number(id)];
    if (!p) return `#${id}`;
    const name = namedIn(p, language) || `#${id}`;
    const tag = [p.letter, count ? `× ${count}` : ''].filter(Boolean).join(' ');
    return `${shownId(id)} – ${tag ? `${name} (${tag})` : name}`;
  };

  // THE HOLES A ROW MAKES IN THE COMPONENT PARTS, one row per piece of hardware's line (see HolesGrid)
  // — the table's one answer about component parts (see TableHole), and what Find holes is sent as
  // the holes to place (see stepHolesPrompt).
  // `side`: which of a mirror pair this row is (see EntrySide) — the pair's part is named as that one.
  // NO TABLE WHERE THERE IS NO HOLE: a row whose lines are all parts met without one (a felt
  // pad's feet) drew a table of names with nothing in its Holes column.
  const holed = (holes: TableHole[]) => holes.some((h) => h.kind !== 'none');
  // ONE WIDTH FOR EVERY ROW'S, column by column (see holeCols): the tables under each other line up.
  const holeLines = (holes: TableHole[], side: EntrySide, washers: Record<string, string>) => (holed(holes) ? (
    <HolesGrid rows={holeRows([{ holes, side, washers }])} items={items} twiceIds={twiceIds} cols={holeCols} />
  // No holes: an EMPTY cell, not a dash — nothing to say is said by saying nothing.
  ) : null);

  const scrollRef = useDragScroll<HTMLDivElement>();
  // THE HOLES TABLES' COLUMN WIDTHS, shared by every row: each column's widest cell, over all of
  // them. Two passes — drawn at their own widths, measured, then drawn at the widest — and again
  // whenever what they hold changes.
  const outer = useRef<HTMLTableElement>(null);
  const [holeCols, setHoleCols] = useState<number[] | null>(null);
  // A NEW MEASURE, asked for. Clearing holeCols alone could not ask: while they were still
  // null — never measured, because the phase was hidden when the table was drawn — setting null
  // again changed nothing, React skipped the render, and they stayed unmeasured for good. That
  // is how every table came to be its own width.
  const [measure, setMeasure] = useState(0);
  const remeasure = () => {
    setHoleCols(null);
    setMeasure((n) => n + 1);
  };
  const holesKey = JSON.stringify([rows, language]);
  useLayoutEffect(remeasure, [holesKey]);
  useLayoutEffect(() => {
    if (holeCols || !outer.current) return;
    const widest: number[] = [];
    // EACH CELL BY ITS COLUMN (`data-col`): under a spanning name or face a row holds fewer
    // cells, and the ones it lacks are in the middle, not at either end. The header's are in order.
    outer.current.querySelectorAll('table[data-holes] tr').forEach((tr) => {
      Array.from(tr.children).forEach((cell, j) => {
        const i = cell.hasAttribute('data-col') ? Number(cell.getAttribute('data-col')) : j;
        widest[i] = Math.max(widest[i] ?? 0, Math.ceil(cell.getBoundingClientRect().width));
      });
    });
    // Nothing to measure while the phase is hidden (every phase stays mounted): wait for it.
    if (widest.some((w) => w > 0)) setHoleCols(widest);
  }, [holeCols, measure]);
  // AND MEASURED AGAIN WHEN IT IS SHOWN: a table drawn hidden has no widths to read.
  useLayoutEffect(() => {
    const el = outer.current;
    if (!el) return;
    let shown = el.getBoundingClientRect().width > 0;
    const watch = new ResizeObserver(() => {
      const now = el.getBoundingClientRect().width > 0;
      if (now && !shown) remeasure();
      shown = now;
    });
    watch.observe(el);
    return () => watch.disconnect();
    // found.busy: the table is unmounted under the spinner, and the one drawn after is a new
    // element to watch.
  }, [rows.length > 0, found.busy]);
  const th = TABLE_HEAD;
  const td = TABLE_CELL;

  return (
    <div className="flex flex-col items-center gap-4">
      {found.error && <p className="text-center text-sm text-rose-600">{found.error}</p>}

      {/* WHILE THE CALL RUNS, THE CARD AND A SPINNER, nothing else: the rows on screen would
          be the last run's answer, about to be replaced, read as if they were this one's. */}
      {found.busy ? (
        <TableCard title={t('notice.steps.subHardware')}>
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        </TableCard>
      ) : rows.length > 0 ? (
        <TableCard title={t('notice.steps.subHardware')}>
          {/* DRAGGED SIDEWAYS as well as scrolled: the table is wider than the page. */}
          <div ref={scrollRef} className="w-full overflow-x-auto">
          <table ref={outer} className={TABLE}>
            <thead>
              <tr>
                <th className={`${th} whitespace-nowrap`}>{t('notice.hardwareTable.colSubsteps')}</th>
                <th className={th}>{t('notice.hardwareTable.colHardware')}</th>
                <th className={th}>{t('notice.hardwareTable.colHow')}</th>
                <th className={th}>{t('notice.hardwareTable.colHoles')}</th>
              </tr>
            </thead>
            <tbody>
              {groups.flatMap((group) => group.map(({ set: one, single, side, substeps }, k) => {
                const span = group.length;
                // THE HARDWARE AND THE SUB-STEPS, once per group, spanning its entries.
                const shared = k === 0 && (
                  <td rowSpan={span} className={td}>
                    {one ? one.hardware.map((id) => (
                      <div key={id} className="whitespace-nowrap">
                        {nameOf(id, sumOf(group.map((g) => g.set?.counts[id])))}
                        {/* WHICH ONE CAPS THE OTHER'S HOLE — where it goes follows from that hole. */}
                        {one.covers.includes(id) && (
                          <span className="ml-1 text-[10px] font-semibold uppercase text-amber-600">
                            {t('notice.hardwareTable.covers')}
                          </span>
                        )}
                      </div>
                    )) : single && (
                      <div className="whitespace-nowrap">
                        {nameOf(single.id, sumOf(group.map((g) => g.single?.entry?.count)))}
                        {/* A COVER STRAIGHT ON A COMPONENT PART — no hole under it. */}
                        {single.entry?.covers && (
                          <span className="ml-1 text-[10px] font-semibold uppercase text-amber-600">
                            {t('notice.hardwareTable.covers')}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                );
                // AND THE HOLES, where none of them makes one: one empty cell, not one each.
                const holesOf = (r: (typeof group)[number]) => (r.set ? r.set.holes : r.single?.entry?.holes ?? []);
                const washersOf = (r: (typeof group)[number]) => r.set?.washers ?? {};
                const noHoles = !group.some((r) => holed(holesOf(r)));
                const holesCell = noHoles
                  ? k === 0 && <td rowSpan={span} className={td} />
                  : <td className={td}>{holeLines(holesOf(group[k]), side, washersOf(group[k]))}</td>;
                const subs = k === 0 && (
                  <td rowSpan={span} className={`${td} tabular-nums`}><Substeps labels={substeps} /></td>
                );
                return one ? (
                  <tr key={`set-${found.interactions.indexOf(one)}-${side}`}>
                    {subs}
                    {shared}
                    <td className={`${td} min-w-[16rem]`}>
                      {(language === 'fr' ? one.descriptionFr : one.descriptionEn)
                        || one.descriptionEn || one.descriptionFr}
                    </td>
                    {holesCell}
                  </tr>
                ) : single && (
                  <tr key={`single-${single.id}-${side}-${k}`}>
                    {subs}
                    {shared}
                    {/* HOW IT GOES IN, when the call said; "no interaction" on an answer from before
                        it was asked. */}
                    {(() => {
                      const a = single.entry;
                      const how = a && ((language === 'fr' ? a.descriptionFr : a.descriptionEn)
                        || a.descriptionEn || a.descriptionFr);
                      return how
                        ? <td className={`${td} min-w-[16rem]`}>{how}</td>
                        : <td className={`${td} text-slate-400`}>{t('notice.hardwareTable.noInteraction')}</td>;
                    })()}
                    {holesCell}
                  </tr>
                );
              }))}
            </tbody>
          </table>
          </div>
        </TableCard>
      ) : (
        <p className="text-center text-sm text-slate-400">
          {t(found.asked ? 'notice.hardwareTable.none' : 'notice.hardwareTable.notYet')}
        </p>
      )}
    </div>
  );
}
